import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ProPaywall from "@/components/ProPaywall";

export default function Friends() {
  const { user } = useAuth();
  const isPro = !!user?.is_pro;
  const [friends, setFriends] = useState([]);
  const [pending, setPending] = useState({ incoming: [], outgoing: [] });
  const [emailInput, setEmailInput] = useState("");
  const [sending, setSending] = useState(false);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const [a, b] = await Promise.all([
      api.get("/friends"),
      api.get("/friends/pending"),
    ]);
    setFriends(a.data.friends);
    setPending(b.data);
  };

  useEffect(() => {
    if (!isPro) { setLoading(false); return; }
    (async () => {
      try { await refresh(); } finally { setLoading(false); }
    })();
  }, [isPro]);

  const sendRequest = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    setSending(true);
    try {
      await api.post("/friends/request", { email });
      toast.success(`Request sent to ${email}`);
      setEmailInput("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally {
      setSending(false);
    }
  };

  const accept = async (fid) => {
    try { await api.post(`/friends/accept/${fid}`); toast.success("Friend added"); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not accept"); }
  };
  const remove = async (fid, label = "Remove") => {
    if (label === "Remove friend" && !confirm("Remove this friend?")) return;
    try { await api.delete(`/friends/${fid}`); await refresh(); toast.success("Done"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const generateInvite = async () => {
    try {
      const { data } = await api.post("/friends/invite-link");
      setInvite(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate link");
    }
  };

  const inviteUrl = invite
    ? `${(process.env.REACT_APP_SHORT_LINK_DOMAIN || window.location.origin).replace(/\/$/, "")}/i/${invite.code}`
    : "";
  const shareText = invite ? `Hey — join me on Shop, Cal & Meal 🍲 ${inviteUrl}` : "";
  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Link copied — paste it anywhere!");
    } catch {
      toast.error("Copy failed — please long-press the link");
    }
  };
  const nativeShare = async () => {
    if (!invite) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Shop, Cal & Meal", text: shareText });
        return;
      } catch { /* user cancelled */ }
    }
    await copyInvite();
  };

  if (!isPro) {
    return (
      <div className="space-y-8 ff-fade-in">
        <header>
          <div className="ff-label">Your kitchen circle</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Favourite friends
          </h1>
        </header>
        <ProPaywall
          feature="Friends is a Pro feature"
          message="Connect with other Pro members and share your favourite recipes with one tap. Upgrade to start cooking together."
        />
      </div>
    );
  }

  if (loading) return <div className="text-[#5C6B61]">Loading friends…</div>;

  return (
    <div className="space-y-8 ff-fade-in" data-testid="friends-page">
      <header>
        <div className="ff-label">Your kitchen circle</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Favourite friends
        </h1>
        <p className="text-[#5C6B61] mt-2 max-w-2xl">
          Connect with other Pro members and share recipes — tap &ldquo;Share&rdquo; on any recipe to send it straight to a friend&apos;s kitchen.
        </p>
      </header>

      <section className="ff-card p-6">
        <div className="ff-label">Add a friend</div>
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          <div>
            <label className="text-xs text-[#5C6B61] font-medium block mb-2">By email</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="friend@email.com"
                className="ff-input text-sm flex-1"
                data-testid="friend-email-input"
                onKeyDown={(e) => e.key === "Enter" && sendRequest()}
              />
              <button
                onClick={sendRequest}
                disabled={sending}
                data-testid="friend-send-request-btn"
                className="ff-btn-primary text-sm disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send request"}
              </button>
            </div>
            <p className="text-[11px] text-[#8B9690] mt-2">They must already be a Pro member.</p>
          </div>
          <div>
            <label className="text-xs text-[#5C6B61] font-medium block mb-2">Or share an invite link</label>
            {!invite ? (
              <button
                onClick={generateInvite}
                data-testid="friend-generate-link-btn"
                className="ff-btn-ghost text-sm border border-[#E5E2DA] w-full"
              >
                🔗 Generate invite link
              </button>
            ) : (
              <div className="flex gap-2 items-center flex-wrap">
                <input
                  readOnly
                  value={inviteUrl}
                  onFocus={(e) => e.target.select()}
                  className="ff-input text-xs flex-1 min-w-[180px]"
                  data-testid="friend-invite-url"
                />
                <button onClick={nativeShare} className="ff-btn-primary text-sm" data-testid="friend-share-link-btn">Share</button>
                <button onClick={copyInvite} className="ff-btn-ghost text-sm border border-[#E5E2DA]" data-testid="friend-copy-link-btn">Copy</button>
              </div>
            )}
            {invite && (
              <p className="text-[11px] text-[#8B9690] mt-2">Anyone Pro who opens this link is instantly added · uses {invite.uses_count}/{invite.max_uses}</p>
            )}
          </div>
        </div>
      </section>

      {(pending.incoming.length + pending.outgoing.length) > 0 && (
        <section className="ff-card p-6" data-testid="friends-pending">
          <div className="ff-label">Pending requests</div>
          <div className="mt-3 space-y-2">
            {pending.incoming.map((p) => (
              <Row
                key={p.friendship_id}
                person={p.other}
                label="wants to be friends"
                actions={
                  <>
                    <button onClick={() => accept(p.friendship_id)} data-testid={`accept-${p.friendship_id}`} className="text-sm font-medium text-[#3B5A3E] hover:underline">Accept</button>
                    <button onClick={() => remove(p.friendship_id, "Reject")} data-testid={`reject-${p.friendship_id}`} className="text-sm text-[#A65541] hover:underline">Reject</button>
                  </>
                }
              />
            ))}
            {pending.outgoing.map((p) => (
              <Row
                key={p.friendship_id}
                person={p.other}
                label="invited · waiting to accept"
                actions={<button onClick={() => remove(p.friendship_id, "Cancel")} className="text-sm text-[#A65541] hover:underline">Cancel</button>}
              />
            ))}
          </div>
        </section>
      )}

      <section className="ff-card p-6" data-testid="friends-list">
        <div className="ff-label">Friends ({friends.length})</div>
        {friends.length === 0 ? (
          <p className="text-sm text-[#5C6B61] mt-3">
            You haven&apos;t added anyone yet. Send an invite link or search by email above.
          </p>
        ) : (
          <div className="mt-3 space-y-2">
            {friends.map((f) => (
              <Row
                key={f.friendship_id}
                person={f}
                label={f.email}
                actions={<button onClick={() => remove(f.friendship_id, "Remove friend")} className="text-sm text-[#A65541] hover:underline" data-testid={`remove-friend-${f.user_id}`}>Remove</button>}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ person, label, actions }) {
  const initials = (person.name || person.email || "?").slice(0, 1).toUpperCase();
  return (
    <div className="flex items-center gap-3 py-2">
      {person.picture ? (
        <img src={person.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-[#3B5A3E] text-white flex items-center justify-center text-sm font-semibold">{initials}</div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[#2A332C] truncate">{person.name || person.email}</div>
        <div className="text-xs text-[#5C6B61] truncate">{label}</div>
      </div>
      <div className="flex gap-3">{actions}</div>
    </div>
  );
}
